import { css } from 'styled-components';
import { darken, math } from 'polished';
import { lg } from '@exonet/react-shared/lib/styled';

// Fonts.
const fallbackFontFamily = `sans-serif, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,Oxygen, Ubuntu, Helvetica Neue, Arial, Apple Color Emoji, Segoe UI Emoji, Segoe UI, Symbol`;

// Font size.
const fontSize = '1rem';

// Colors.
export const red = '#e2032d';
export const green = '#2cbe4e';
export const orange = '#ff9500';
export const blue = '#4886BE';
export const grey = '#838383';

// Padding.
const xxsPaddingVertical = '0.2rem';
const xxsPaddingHorizontal = '0.4rem';
const xsPaddingVertical = '0.5rem';
const xsPaddingHorizontal = '0.75rem';
const smPaddingVertical = '0.75rem';
const smPaddingHorizontal = '1.125rem';
const lgPaddingVertical = '1rem';
const lgPaddingHorizontal = '1.5rem';
const xlPaddingVertical = '1.375rem';
const xlPaddingHorizontal = '1.875rem';

export default css`
  // Colors
  --red: ${red};
  --green: ${green};
  --orange: ${orange};
  --blue: ${blue};
  --grey: ${grey};

  --text-white: #ffffff;

  // Font family
  --font-family: 'Exo', ${fallbackFontFamily};
  --font-family-2: 'Exo 2', ${fallbackFontFamily};

  // Font sizes
  --font-size: ${fontSize};
  --xs-font-size: ${math(`${fontSize} * 0.65`)};
  --sm-font-size: ${math(`${fontSize} * 0.85`)};
  --lg-font-size: ${math(`${fontSize} * 1.2`)};
  --line-height: 1.5;

  // Spacing
  --xxs-padding: ${xxsPaddingVertical} ${xxsPaddingHorizontal};
  --xxs-padding-vertical: ${xxsPaddingVertical};
  --xxs-padding-horizontal: ${xxsPaddingHorizontal};

  --xs-padding: ${xsPaddingVertical} ${xsPaddingHorizontal};
  --xs-padding-vertical: ${xsPaddingVertical};
  --xs-padding-horizontal: ${xsPaddingHorizontal};

  --sm-padding: ${smPaddingVertical} ${smPaddingHorizontal};
  --sm-padding-vertical: ${smPaddingVertical};
  --sm-padding-horizontal: ${smPaddingHorizontal};

  --lg-padding: ${lgPaddingVertical} ${lgPaddingHorizontal};
  --lg-padding-vertical: ${lgPaddingVertical};
  --lg-padding-horizontal: ${lgPaddingHorizontal};

  --xl-padding: ${xlPaddingVertical} ${xlPaddingHorizontal};
  --xl-padding-vertical: ${xlPaddingVertical};
  --xl-padding-horizontal: ${xlPaddingHorizontal};

  // Layout.
  --border-radius: 0.25rem;

  // Table
  --table-padding: 0.75rem;
  --table-header-position: sticky;
  --table-xs-sticky-header-pos: 0;
  --table-sm-sticky-header-pos: var(--xs-header-height);
  --table-lg-sticky-header-pos: var(--xs-header-height);

  ${lg(css`
    --table-lg-sticky-header-pos: 0;
  `)}

  // Button
  --btn-padding: calc(var(--xs-padding-vertical) + 2px) var(--xs-padding-horizontal);
  --btn-padding-vertical: calc(var(--xs-padding-vertical) + 2px);
  --btn-padding-horizontal: var(--xs-padding-horizontal);
  --btn-font-size: var(--sm-font-size);
  --btn-border-radius: 0.15rem;
  --btn-box-shadow: var(--box-shadow-component);

  --btn-bg: #6c757d;
  --btn-bg-hover: #5c636a;
  --btn-text: #fff;

  --btn-primary-bg: #ff4a5b;
  --btn-primary-bg-hover: var(--red);
  --btn-primary-text: #fff;

  --btn-alt-bg: ${green};
  --btn-alt-bg-hover: ${darken(0.05, green)};
  --btn-alt-text: #fff;

  // Form
  --form-label-text-transform: normal;
  --form-label-font-size: var(--sm-font-size);
  --form-label-margin-bottom: var(--xs-padding-vertical);
  --form-button-font-size: var(--sm-font-size);
  --form-button-padding: var(--btn-padding);
  --form-button-height: auto;
  --form-button-min-height: auto;
  --form-button-border-width: var(--btn-border-width);
  --form-button-border-radius: var(--btn-border-radius);
  --form-button-bg: var(--btn-alt-bg);
  --form-button-bg-hover: var(--btn-alt-bg-hover);
  --form-button-text: var(--btn-text);
  --form-button-box-shadow: var(--btn-box-shadow);
  --form-input-padding: var(--xs-padding);
  --form-input-font-size: var(--font-size);
  --form-input-border-width: 1px;
  --form-input-border-radius: var(--border-radius);
  --form-select-height: var(--select-height);
  --form-select-placeholder: var(--select-placeholder);
  --form-select-text: var(--select-text);
  --form-select-bg: var(--select-bg);
  --form-select-option-bg: var(--select-option-bg);
  --form-select-option-hover-bg: var(--select-option-hover-bg);
  --form-select-option-selected: var(--select-option-selected);
  --form-select-option-selected-bg: var(--select-option-selected-bg);
  --form-select-option-disabled: var(--select-option-disabled);
  --form-select-option-disabled-bg: var(--select-option-disabled-bg);
  --form-select-multi-value-border: var(--select-multi-value-border);
  --form-select-multi-value-bg: var(--select-multi-value-bg);
  --form-select-border-width: var(--select-border-width);
  --form-select-border-style: var(--select-border-style);
  --form-select-border-color: var(--select-border-color);
  --form-select-border-radius: var(--select-border-radius);
  --form-select-icon: var(--select-icon);
  --form-checkbox-border-color: transparent;

  // z-index
  --base-z-index: 100;
  --menu-z-index: 800;
  --header-z-index: 900;

  // Heights
  --xs-header-height: 3.125rem;
  --lg-header-height: 4.5rem;

  // Icon
  --icon-size: 1.25rem;

  // Table
  --table-position: relative;
  --table-box-shadow: var(--box-shadow);

  // Data-manager
  // Select.
  --data-manager-select-height: var(--form-select-height);

  // Controls section: pinned on both the real section and the skeleton so
  // small per-control height differences can never shift the table on load.
  --data-manager-controls-min-height: 58px;

  // Header: pinned the same way, so headers with and without action buttons
  // (and their skeletons) are always the same height.
  --data-manager-header-min-height: 48px;

  // Range picker
  --data-manager-range-picker-width: 200px;
  --datepicker-color: var(--text);
  --datepicker-primary-color: var(--link);
  --datepicker-text-color: var(--data-manager-select-text);
  --datepicker-selected-text-color: var(--text-white);
  --datepicker-day-selected-bg: var(--link-hover);
  --datepicker-day-selected-bg-middle: var(--link);
  --datepicker-input-width: 200px;
  --datepicker-input-height: var(--form-select-height);

  // Search.
  --data-manager-search-bg: var(--form-input-bg);
  --data-manager-search-text: var(--form-input-text);
  --data-manager-placeholer-text: var(--form-field-placeholder);
  --data-manager-search-border-width: var(--form-input-border-width);
  --data-manager-search-border-color: var(--form-input-border-color);
  --data-manager-search-border-radius: var(--form-input-border-radius);
  --data-manager-search-box-shadow: var(--btn-box-shadow);
  --data-manager-search-placeholder: var(--form-field-placeholder);

  // Action Select
  --data-manager-action-select-bg: var(--btn-alt-bg);
  --data-manager-action-select-control-bg: var(--btn-alt-bg);
  --data-manager-action-select-text: #fff;
  --data-manager-action-select-border-color: var(--btn-alt-bg);
  --data-manager-action-select-option-hover: #fff;
  --data-manager-action-select-option-hover-bg: var(--btn-alt-bg-hover);

  // Filter button
  --data-manager-filter-button-bg: var(--data-manager-select-bg);
  --data-manager-filter-button-border: var(--bg-7);

  // Label-list
  --label-list-title-font-size: var(--font-size);
  --label-list-font-size: var(--sm-font-size);
  --label-list-border-radius: var(--border-radius);
  --label-list-box-shadow: var(--box-shadow);
  --label-list-bg: var(--bg);

  // Boolean
  --boolean-true: ${green};
  --boolean-false: ${red};

  // Focus-ring
  --focus-ring-color: var(--link);
`;
